import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  FiEye,
  FiEyeOff,
  FiSearch,
  FiCheck,
  FiX,
  FiShield,
  FiUser,
  FiUsers,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronsLeft,
  FiChevronsRight,
  FiEdit2,
  FiTrash2,
  FiLock,
  FiMapPin,
  FiUserCheck,
  FiFolder,
  FiFileText,
  FiLayers,
  FiCheckSquare,
  FiSquare,
  FiMinusSquare,
  FiSliders,
} from "react-icons/fi";

import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import useInfiniteTable from "../../shared/useInfiniteTable";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import { useDbContext } from "../../../context/DbContext";
import { selectActiveLocation } from "../../../app/locationSlice";
import { selectMenuPermissions } from "../../../app/permissionsSlice";

function CreateUser({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const activeLocation = useSelector(selectActiveLocation);

  // System Action Permissions for CreateUser form
  const permissions = useSelector(selectMenuPermissions);
  const formPermissions = permissions?.["createUser"]?.actions || {};
  const hasSavePermission = formPermissions.Save ?? true;
  const hasEditPermission = formPermissions.Edit ?? true;
  const hasDeletePermission = formPermissions.Delete ?? true;
  const hasFindPermission = formPermissions.Find ?? true;

  // Active Tab: "group" (Role & Permissions) or "user" (User Accounts)
  const [activeTab, setActiveTab] = useState("group");

  // Edit Tracking States
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Password Visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Tree Expansion and Search Filter
  const [expandedNodes, setExpandedNodes] = useState({});
  const [menuSearchQuery, setMenuSearchQuery] = useState("");
  const [userDirectorySearch, setUserDirectorySearch] = useState("");
  const [isTreeLoading, setIsTreeLoading] = useState(false);

  // Group Object State
  const [groupData, setGroupData] = useState({
    groupName: null, // { value, label } or string
    savePermission: false,
    findPermission: false,
    editPermission: false,
    deletePermission: false,
    MenuPermissions: {},
  });

  // User Object State
  const [userData, setUserData] = useState({
    userName: null, // { value, label } or string
    password: "",
    confirmPassword: "",
    underGroup: null, // { value, label }
    employee: null, // { value, label }
    location: null, // { value, label }
  });

  // Directory Options
  const [usersList, setUsersList] = useState([]);
  const [groupsList, setGroupsList] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [menuTree, setMenuTree] = useState([]);

  // Full detailed users list for the directory table
  const [fullUsersDirectory, setFullUsersDirectory] = useState([]);

  // Input Refs for sequential Enter navigation
  const userNameRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const underGroupRef = useRef(null);
  const employeeRef = useRef(null);
  const locationRef = useRef(null);

  // Standard input styling
  const inputClass =
    "w-full px-2.5 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400";

  // Fetch Users
  const fetchUsers = useCallback(async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/common/SelectUserID/${dbCode}`);
      const allUsers = response.data || [];
      const validUsers = allUsers.filter(
        (item) => item.userName && String(item.userName).trim() !== ""
      );
      const targetUsers = validUsers.length > 0 ? validUsers : allUsers;
      const formatted = targetUsers.map((item) => ({
        value: item.auto ?? item.Auto,
        label: item.userName || item.UserName || `User #${item.auto}`,
      }));
      setUsersList(formatted);
      setFullUsersDirectory(targetUsers);
    } catch (error) {
      console.error("Error fetching userids:", error);
    }
  }, [apiBaseUrl, dbCode]);

  // Fetch Groups
  const fetchGroups = useCallback(async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/common/SelectUserGroup/${dbCode}`);
      const rawGroups = response.data || [];
      const formatted = rawGroups.map((item) => ({
        value: item.Auto,
        label: item.Name,
        ...item,
      }));
      setGroupsList(formatted);
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  }, [apiBaseUrl, dbCode]);

  // Fetch Employees / Salesmen
  const fetchEmployees = useCallback(async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/common/selectAllSalesMan/${dbCode}`);
      const rawEmp = response.data || [];
      const formatted = rawEmp.map((item) => ({
        value: item.Auto,
        label: item.Name,
      }));
      setEmployeesList(formatted);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  }, [apiBaseUrl, dbCode]);

  // Fetch Locations
  const fetchLocations = useCallback(async () => {
    if (!dbCode) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`);
      const records = response.data || [];
      const locationRecords = records.filter(
        (r) => String(r.Type || r.type || "").toLowerCase() === "location"
      );
      const formatted = locationRecords.map((r) => ({
        value: Number(r.auto ?? r.Auto ?? 0),
        label: r.Name || r.name,
      }));
      setLocationsList(formatted);

      // Pre-select active logged-in branch if available and not yet set
      const loggedInLocId =
        activeLocation?.id ||
        sessionStorage.getItem("loginLocationId") ||
        localStorage.getItem("loginLocationId");
      if (loggedInLocId) {
        const found = formatted.find((loc) => Number(loc.value) === Number(loggedInLocId));
        if (found) {
          setUserData((prev) => (prev.location ? prev : { ...prev, location: found }));
        }
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  }, [apiBaseUrl, dbCode, activeLocation]);

  // Initial Load (runs on mount or dbCode switch)
  useEffect(() => {
    if (dbCode) {
      fetchUsers();
      fetchGroups();
      fetchEmployees();
      fetchLocations();
    }
  }, [dbCode, fetchUsers, fetchGroups, fetchEmployees, fetchLocations]);

  // Fetch Menu Tree for a specific role / group ID
  const fetchMenuTree = async (groupId) => {
    if (!groupId) return;
    setIsTreeLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/common/getMenuTree/${dbCode}/${groupId}`);
      if (response.data.success) {
        const tree = response.data.tree || [];
        const newPermissions = {};

        // Recursively populate permissions from database response
        const traverse = (nodes) => {
          nodes.forEach((node) => {
            newPermissions[node.id] = node.visible ?? false;
            if (node.actions) {
              node.actions.forEach((action) => {
                newPermissions[`${node.id}_${action.name}`] = action.isEnabled ?? false;
              });
            }
            if (node.children) traverse(node.children);
          });
        };

        traverse(tree);
        setMenuTree(tree);
        setGroupData((prev) => ({
          ...prev,
          MenuPermissions: newPermissions,
        }));

        // Automatically expand top-level categories
        const initialExpand = {};
        tree.forEach((t) => {
          initialExpand[t.id] = true;
        });
        setExpandedNodes(initialExpand);
      }
    } catch (error) {
      console.error("Error fetching menu tree:", error);
    } finally {
      setIsTreeLoading(false);
    }
  };

  // Toggle single node expand/collapse
  const toggleNodeExpansion = (nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  // Expand all nodes
  const expandAllNodes = () => {
    const all = {};
    const traverse = (nodes) => {
      nodes.forEach((n) => {
        all[n.id] = true;
        if (n.children) traverse(n.children);
      });
    };
    traverse(menuTree);
    setExpandedNodes(all);
  };

  // Collapse all nodes
  const collapseAllNodes = () => {
    setExpandedNodes({});
  };

  // Tree traversal helper
  const traverseTree = (nodes, callback) => {
    nodes.forEach((node) => {
      callback(node);
      if (node.children) traverseTree(node.children, callback);
    });
  };

  // Select / Unselect All permissions across entire tree
  const handleSelectAll = (selectVal) => {
    const newPerm = { ...groupData.MenuPermissions };
    traverseTree(menuTree, (node) => {
      newPerm[node.id] = selectVal;
      if (node.actions) {
        node.actions.forEach((action) => {
          newPerm[`${node.id}_${action.name}`] = selectVal;
        });
      }
    });
    setGroupData((prev) => ({ ...prev, MenuPermissions: newPerm }));
  };

  // Select all of a specific action across all forms (e.g. Save, Edit, Delete, Find)
  const handleSelectAllAction = (actionName, selectVal = true) => {
    const newPerm = { ...groupData.MenuPermissions };
    traverseTree(menuTree, (node) => {
      if (node.actions && node.actions.some((a) => a.name === actionName)) {
        newPerm[`${node.id}_${actionName}`] = selectVal;
        if (selectVal) {
          // If enabling an action, ensure form is also visible
          newPerm[node.id] = true;
        }
      }
    });
    setGroupData((prev) => ({ ...prev, MenuPermissions: newPerm }));
  };

  // Cascading toggle for a parent menu node: toggles self, all descendant menus, and actions
  const handleCascadeToggle = (node, isChecked) => {
    const newPerm = { ...groupData.MenuPermissions };
    const cascade = (currNode) => {
      newPerm[currNode.id] = isChecked;
      if (currNode.actions) {
        currNode.actions.forEach((a) => {
          newPerm[`${currNode.id}_${a.name}`] = isChecked;
        });
      }
      if (currNode.children) {
        currNode.children.forEach(cascade);
      }
    };
    cascade(node);
    setGroupData((prev) => ({ ...prev, MenuPermissions: newPerm }));
  };

  // Toggle single action permission
  const handleActionPermissionChange = (menuId, actionName, isChecked) => {
    setGroupData((prev) => {
      const next = {
        ...prev.MenuPermissions,
        [`${menuId}_${actionName}`]: isChecked,
      };
      // If turning on an action, ensure parent menu visibility is active
      if (isChecked) {
        next[menuId] = true;
      }
      return { ...prev, MenuPermissions: next };
    });
  };

  // Load a group into editor
  const handleLoadGroup = async (groupItem) => {
    const groupId = groupItem.value || groupItem.Auto;
    const groupName = groupItem.label || groupItem.Name;
    try {
      const response = await axios.post(`${apiBaseUrl}/common/findGroup/${dbCode}`, {
        groupId,
      });
      if (response.data.success) {
        const g = response.data.group;
        setGroupData({
          groupName: { value: g.Auto, label: g.Name },
          savePermission: g.S === 1,
          findPermission: g.F === 1,
          editPermission: g.E === 1,
          deletePermission: g.D === 1,
          MenuPermissions: {},
        });
        setSelectedGroupId(g.Auto);
        setIsEditingGroup(true);
        fetchMenuTree(g.Auto);
      }
    } catch (err) {
      console.error("Error loading group:", err);
    }
  };

  // Load a user into editor
  const handleLoadUser = async (userItem) => {
    const uName = typeof userItem === "object" ? userItem.label || userItem.userName : userItem;
    if (!uName) return;
    try {
      const response = await axios.post(`${apiBaseUrl}/common/findUser/${dbCode}`, {
        userName: uName,
      });
      if (response.data.success) {
        const u = response.data.user;
        const matchedGroup = groupsList.find(
          (g) => g.label.toLowerCase() === String(u.userType || "").toLowerCase()
        ) || (u.userType ? { value: u.userType, label: u.userType } : null);
        const matchedEmp = employeesList.find((e) => e.value === u.agentCode) || null;
        const locVal = u.location ?? u.Location;
        const matchedLoc = locationsList.find((l) => Number(l.value) === Number(locVal)) || null;

        setUserData({
          userName: { value: u.auto, label: u.userName },
          password: "",
          confirmPassword: "",
          underGroup: matchedGroup,
          employee: matchedEmp,
          location: matchedLoc,
        });
        setSelectedUserId(u.auto);
        setIsEditingUser(true);
      }
    } catch (err) {
      console.error("Error finding user:", err);
    }
  };

  // Save / Update Group
  const saveGroup = async () => {
    if (isEditingGroup && !hasEditPermission) {
      alert("You do not have permission to edit user groups");
      return;
    }
    if (!isEditingGroup && !hasSavePermission) {
      alert("You do not have permission to create user groups");
      return;
    }
    const nameStr =
      typeof groupData.groupName === "object" && groupData.groupName !== null
        ? groupData.groupName.label
        : String(groupData.groupName || "").trim();

    if (!nameStr) {
      alert("Please enter a role / group name");
      return;
    }

    try {
      let response;
      const payload = {
        groupName: nameStr,
        save: groupData.savePermission ? 1 : 0,
        find: groupData.findPermission ? 1 : 0,
        edit: groupData.editPermission ? 1 : 0,
        delete: groupData.deletePermission ? 1 : 0,
        permissions: groupData.MenuPermissions,
        groupId: selectedGroupId,
      };

      if (isEditingGroup && selectedGroupId) {
        response = await axios.post(`${apiBaseUrl}/common/updateGroup/${dbCode}`, payload);
      } else {
        response = await axios.post(`${apiBaseUrl}/common/saveGroup/${dbCode}`, payload);
      }

      if (response.data.success) {
        alert(isEditingGroup ? "Role updated successfully!" : "Role created successfully!");
        fetchGroups();
        handleClearGroup();
      } else {
        alert(response.data.message || "Failed to save role");
      }
    } catch (err) {
      console.error("Error saving group:", err);
      alert("Error saving group permissions");
    }
  };

  // Save / Update User
  const saveUser = async () => {
    if (isEditingUser && !hasEditPermission) {
      alert("You do not have permission to edit users");
      return;
    }
    if (!isEditingUser && !hasSavePermission) {
      alert("You do not have permission to create users");
      return;
    }

    const uName =
      typeof userData.userName === "object" && userData.userName !== null
        ? userData.userName.label
        : String(userData.userName || "").trim();

    if (!uName) {
      alert("Please enter or select a username");
      return;
    }
    if (!userData.underGroup) {
      alert("Please assign a role / group to this user");
      return;
    }
    if (!userData.employee) {
      alert("Please link an employee / salesman to this user");
      return;
    }
    if (!isEditingUser && !userData.password) {
      alert("Please set a password for the new user");
      return;
    }
    if (userData.password && userData.password !== userData.confirmPassword) {
      alert("Password and Confirm Password do not match");
      return;
    }

    try {
      let response;
      const payload = {
        userName: uName,
        password: userData.password || undefined,
        groupName: userData.underGroup.label || userData.underGroup.value,
        employee: userData.employee.value,
        location: userData.location?.value || 0,
      };

      if (isEditingUser && selectedUserId) {
        response = await axios.post(`${apiBaseUrl}/common/updateUser/${dbCode}`, payload);
      } else {
        response = await axios.post(`${apiBaseUrl}/common/saveUser/${dbCode}`, payload);
      }

      if (response.data.success) {
        alert(isEditingUser ? "User updated successfully!" : "User created successfully!");
        fetchUsers();
        handleClearUser();
      } else {
        alert(response.data.message || "Failed to save user");
      }
    } catch (err) {
      console.error("Error saving user:", err);
      alert("Error saving user profile");
    }
  };

  // Delete Group
  const deleteGroup = async () => {
    if (!hasDeletePermission) {
      alert("You do not have permission to delete user groups");
      return;
    }
    const nameStr =
      typeof groupData.groupName === "object" && groupData.groupName !== null
        ? groupData.groupName.label
        : String(groupData.groupName || "").trim();

    if (!nameStr) {
      alert("Please select a role to delete");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete role "${nameStr}" and all its permissions?`)) {
      return;
    }

    try {
      const response = await axios.post(`${apiBaseUrl}/common/deleteGroup/${dbCode}`, {
        groupName: nameStr,
      });
      if (response.data.success) {
        alert("Role deleted successfully");
        fetchGroups();
        handleClearGroup();
      } else {
        alert(response.data.message || "Failed to delete role");
      }
    } catch (err) {
      console.error("Error deleting group:", err);
      alert("Error deleting role");
    }
  };

  // Delete User
  const deleteUser = async () => {
    if (!hasDeletePermission) {
      alert("You do not have permission to delete users");
      return;
    }
    const uName =
      typeof userData.userName === "object" && userData.userName !== null
        ? userData.userName.label
        : String(userData.userName || "").trim();

    if (!uName) {
      alert("Please select a user to delete");
      return;
    }
    if (!window.confirm(`Are you sure you want to permanently delete user "${uName}"?`)) {
      return;
    }

    try {
      const response = await axios.post(`${apiBaseUrl}/common/deleteUser/${dbCode}`, {
        userName: uName,
      });
      if (response.data.success) {
        alert("User deleted successfully");
        fetchUsers();
        handleClearUser();
      } else {
        alert(response.data.message || "Failed to delete user");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      alert("Error deleting user");
    }
  };

  // Clear Group Form
  const handleClearGroup = () => {
    setGroupData({
      groupName: null,
      savePermission: false,
      findPermission: false,
      editPermission: false,
      deletePermission: false,
      MenuPermissions: {},
    });
    setIsEditingGroup(false);
    setSelectedGroupId(null);
    setMenuTree([]);
    setExpandedNodes({});
    setMenuSearchQuery("");
  };

  // Clear User Form
  const handleClearUser = () => {
    const loggedInLocId =
      activeLocation?.id ||
      sessionStorage.getItem("loginLocationId") ||
      localStorage.getItem("loginLocationId");
    const defaultLoc = locationsList.find((loc) => Number(loc.value) === Number(loggedInLocId)) || null;

    setUserData({
      userName: null,
      password: "",
      confirmPassword: "",
      underGroup: null,
      employee: null,
      location: defaultLoc,
    });
    setIsEditingUser(false);
    setSelectedUserId(null);
  };

  // Universal Clear for Toolbar
  const clearForm = () => {
    if (activeTab === "group") handleClearGroup();
    else handleClearUser();
  };

  // Filtered Menu Tree based on search query
  const filteredTree = useMemo(() => {
    if (!menuSearchQuery.trim()) return menuTree;
    const q = menuSearchQuery.toLowerCase().trim();

    const filterNodes = (nodes) => {
      const result = [];
      nodes.forEach((node) => {
        const matchesName = node.name.toLowerCase().includes(q);
        const matchesAction = node.actions?.some((a) => a.name.toLowerCase().includes(q));
        const filteredChildren = node.children ? filterNodes(node.children) : [];

        if (matchesName || matchesAction || filteredChildren.length > 0) {
          result.push({
            ...node,
            children: filteredChildren,
          });
        }
      });
      return result;
    };

    return filterNodes(menuTree);
  }, [menuTree, menuSearchQuery]);

  // Count active permissions granted
  const grantedStats = useMemo(() => {
    let granted = 0;
    let total = 0;
    traverseTree(menuTree, (node) => {
      total += 1;
      if (groupData.MenuPermissions[node.id]) granted += 1;
      if (node.actions) {
        node.actions.forEach((a) => {
          total += 1;
          if (groupData.MenuPermissions[`${node.id}_${a.name}`]) granted += 1;
        });
      }
    });
    return { granted, total };
  }, [menuTree, groupData.MenuPermissions]);

  // O(1) Lookup Maps for High-Performance Rendering of large datasets
  const employeesMap = useMemo(() => {
    const map = new Map();
    employeesList.forEach((e) => map.set(e.value, e.label));
    return map;
  }, [employeesList]);

  const locationsMap = useMemo(() => {
    const map = new Map();
    locationsList.forEach((l) => map.set(Number(l.value), l.label));
    return map;
  }, [locationsList]);

  // Filtered Users Directory Table
  const filteredUsers = useMemo(() => {
    if (!userDirectorySearch.trim()) return fullUsersDirectory;
    const q = userDirectorySearch.toLowerCase().trim();
    return fullUsersDirectory.filter((u) => {
      const un = String(u.userName || "").toLowerCase();
      const ut = String(u.userType || "").toLowerCase();
      return un.includes(q) || ut.includes(q);
    });
  }, [fullUsersDirectory, userDirectorySearch]);

  // Infinite Continuous Scrolling Table hook (matching EventDetailsReport and LedgerList)
  const {
    displayedRows: displayedUsers,
    visibleCount,
    tableContainerRef,
    handleScroll,
    handleKeyDown: handleTableKeyDown,
    loadAll,
    hasMore,
  } = useInfiniteTable({
    items: filteredUsers,
    initialCount: 100,
    step: 100,
  });

  // Calculate selection state (all, partial, none) for a node and all its descendants
  const getNodeSelectionState = useCallback(
    (currNode) => {
      let totalItems = 0;
      let selectedItems = 0;

      const count = (n) => {
        totalItems += 1;
        if (groupData.MenuPermissions[n.id]) selectedItems += 1;
        if (n.actions) {
          n.actions.forEach((a) => {
            totalItems += 1;
            if (groupData.MenuPermissions[`${n.id}_${a.name}`]) selectedItems += 1;
          });
        }
        if (n.children) {
          n.children.forEach(count);
        }
      };

      count(currNode);

      if (selectedItems === 0) return "none";
      if (selectedItems === totalItems) return "all";
      return "partial";
    },
    [groupData.MenuPermissions]
  );

  // Calculate form count and active form count under a parent node
  const getNodeChildSummary = useCallback(
    (currNode) => {
      let formCount = 0;
      let activeFormCount = 0;

      const scan = (n) => {
        if (!n.children || n.children.length === 0) {
          formCount += 1;
          if (groupData.MenuPermissions[n.id]) activeFormCount += 1;
        } else {
          n.children.forEach(scan);
        }
      };

      if (currNode.children) {
        currNode.children.forEach(scan);
      }

      return { formCount, activeFormCount };
    },
    [groupData.MenuPermissions]
  );

  // Toggle all actions for a specific leaf form node
  const handleToggleAllActionsForNode = (node) => {
    if (!node.actions || node.actions.length === 0) return;
    const allActive = node.actions.every((a) =>
      Boolean(groupData.MenuPermissions[`${node.id}_${a.name}`])
    );
    const target = !allActive;

    setGroupData((prev) => {
      const next = { ...prev.MenuPermissions };
      if (target) {
        next[node.id] = true;
      }
      node.actions.forEach((a) => {
        next[`${node.id}_${a.name}`] = target;
      });
      return { ...prev, MenuPermissions: next };
    });
  };

  // Search keyword highlight helper using pure Tailwind CSS & Inter font
  const highlightMatch = (text, query) => {
    if (!query || !query.trim()) return text;
    const q = query.trim();
    const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(regex);
    return (
      <span className="font-Inter">
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark
              key={i}
              className="bg-amber-200 text-amber-950 rounded-[3px] px-0.5 font-bold font-Inter"
            >
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  // Advanced Recursive Tree Node Component
  const TreeNode = ({ node, depth = 0 }) => {
    const isExpanded = expandedNodes[node.id] || Boolean(menuSearchQuery);
    const hasChildren = node.children && node.children.length > 0;
    const hasActions = node.actions && node.actions.length > 0;
    const selectionState = getNodeSelectionState(node);
    const summary = hasChildren ? getNodeChildSummary(node) : null;

    const handleCheckboxClick = (e) => {
      e.stopPropagation();
      // If already all selected -> unselect. If none or partial -> select all
      const targetChecked = selectionState !== "all";
      handleCascadeToggle(node, targetChecked);
    };

    const isRoot = depth === 0;
    const isSubCategory = depth === 1 && hasChildren;

    // Action all state for leaf form
    const allActionsActive =
      hasActions &&
      node.actions.every((a) =>
        Boolean(groupData.MenuPermissions[`${node.id}_${a.name}`])
      );

    return (
      <div
        className={`flex flex-col font-Inter ${
          isRoot
            ? "mb-2 border border-slate-200/90 rounded-lg bg-white overflow-hidden shadow-xs hover:border-blue-300 transition-all"
            : "py-1"
        }`}
      >
        {/* Node Row Header */}
        <div
          className={`flex items-center justify-between transition-colors select-none ${
            isRoot
              ? "px-3 py-2 bg-slate-50/80 hover:bg-blue-50/50 cursor-pointer border-b border-slate-200/60"
              : isSubCategory
              ? "px-2 py-1.5 hover:bg-slate-100/70 rounded-md cursor-pointer"
              : "px-2 py-1 hover:bg-blue-50/50 rounded-md"
          }`}
          onClick={
            hasChildren
              ? () => toggleNodeExpansion(node.id)
              : undefined
          }
        >
          {/* Left section: expand icon, checkbox, node icon, title, summary pill */}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Expand / Collapse Button */}
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNodeExpansion(node.id);
                }}
                className="w-5 h-5 flex items-center justify-center text-slate-500 hover:text-blue-600 hover:bg-slate-200/60 rounded cursor-pointer transition-colors"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <FiChevronDown className="text-sm" />
                ) : (
                  <FiChevronRight className="text-sm" />
                )}
              </button>
            ) : (
              <span className="w-5" />
            )}

            {/* Smart Checkbox Icon (All / Partial / None) */}
            <button
              type="button"
              onClick={handleCheckboxClick}
              className="flex items-center justify-center focus:outline-none cursor-pointer"
              title={
                selectionState === "all"
                  ? "Unselect all in this group"
                  : "Grant access to all in this group"
              }
            >
              {selectionState === "all" ? (
                <FiCheckSquare className="text-blue-600 text-[15px]" />
              ) : selectionState === "partial" ? (
                <FiMinusSquare className="text-blue-600 text-[15px]" />
              ) : (
                <FiSquare className="text-slate-300 hover:text-slate-500 text-[15px]" />
              )}
            </button>

            {/* Visual Node Type Icon */}
            {isRoot ? (
              <FiLayers className="text-blue-600 text-sm shrink-0" />
            ) : hasChildren ? (
              <FiFolder className="text-amber-500 text-xs shrink-0" />
            ) : (
              <FiFileText className="text-slate-400 text-xs shrink-0" />
            )}

            {/* Node Label / Title */}
            <span
              className={`truncate font-Inter ${
                isRoot
                  ? "text-xs font-bold text-slate-900 tracking-tight"
                  : isSubCategory
                  ? "text-xs font-semibold text-slate-800"
                  : "text-xs font-medium text-slate-700"
              }`}
            >
              {highlightMatch(node.name, menuSearchQuery)}
            </span>

            {/* Summary Badge for Parent Categories */}
            {hasChildren && summary && (
              <span
                className={`ml-1 text-[10px] font-bold px-1.5 py-0.2 rounded-full border transition-all ${
                  summary.activeFormCount === summary.formCount && summary.formCount > 0
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : summary.activeFormCount > 0
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-slate-100 text-slate-500 border-slate-200"
                }`}
              >
                {summary.activeFormCount === summary.formCount && summary.formCount > 0
                  ? "All active"
                  : `${summary.activeFormCount} / ${summary.formCount} forms`}
              </span>
            )}
          </div>

          {/* Right section: Action Buttons for Form Leaf Nodes */}
          {hasActions && (
            <div
              className="flex items-center gap-1.5 shrink-0 ml-3"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Form All Actions Quick Toggle */}
              <button
                type="button"
                onClick={() => handleToggleAllActionsForNode(node)}
                title={
                  allActionsActive
                    ? "Revoke all actions for this form"
                    : "Grant all actions for this form"
                }
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border transition-all cursor-pointer ${
                  allActionsActive
                    ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                    : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200"
                }`}
              >
                <FiSliders className="text-[10px]" />
                <span>All</span>
              </button>

              {/* Individual Form Action Chips (Save, Edit, Delete, Find, etc.) */}
              {node.actions.map((act) => {
                const actKey = `${node.id}_${act.name}`;
                const isActChecked = Boolean(groupData.MenuPermissions[actKey]);
                let badgeClass =
                  "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-slate-700";

                if (isActChecked) {
                  if (act.name === "Save") {
                    badgeClass = "bg-emerald-600 text-white border-emerald-600 shadow-xs";
                  } else if (act.name === "Edit") {
                    badgeClass = "bg-amber-600 text-white border-amber-600 shadow-xs";
                  } else if (act.name === "Delete") {
                    badgeClass = "bg-rose-600 text-white border-rose-600 shadow-xs";
                  } else if (act.name === "Find") {
                    badgeClass = "bg-blue-600 text-white border-blue-600 shadow-xs";
                  } else {
                    badgeClass = "bg-indigo-600 text-white border-indigo-600 shadow-xs";
                  }
                }

                return (
                  <button
                    key={actKey}
                    type="button"
                    onClick={() =>
                      handleActionPermissionChange(node.id, act.name, !isActChecked)
                    }
                    title={`Click to ${isActChecked ? "disable" : "enable"} ${
                      act.name
                    } permission for ${node.name}`}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold border transition-all cursor-pointer select-none ${badgeClass}`}
                  >
                    {act.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Children Render with Indentation and Subtle Guide Line */}
        {isExpanded && hasChildren && (
          <div
            className={`flex flex-col ${
              isRoot
                ? "px-3 py-2 border-t border-slate-100 bg-white"
                : "border-l border-slate-200/90 pl-3.5 ml-3.5 mt-0.5 gap-0.5"
            }`}
          >
            {node.children.map((child) => (
              <TreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="bg-[#d2e4fc] w-full h-full rounded-none shadow-2xl flex flex-col items-center absolute left-0 top-0 p-[1%] font-Inter select-none"
      style={{ zIndex }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      {/* Main Container - Rounded White Card */}
      <div className="w-full flex-1 bg-white border border-[#b8cce0] rounded-[10px] shadow-sm flex flex-col overflow-hidden">
        {/* Top Header */}
        <WindowHeader
          title="User & Access Control Management"
          logo={headeLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        {/* Standard Action Toolbar */}
        <div className="w-full shrink-0 border-b border-slate-200 bg-slate-50">
          <ActionToolbar
            width="100%"
            height="auto"
            bgColor="#F8FAFC"
            size="small"
            iconSize={16}
            showSave={hasSavePermission}
            showPrint={false}
            showClear={true}
            showFind={false}
            showEdit={hasEditPermission}
            showDelete={hasDeletePermission}
            showExit={true}
            showNavigation={false}
            saveDisabled={!hasSavePermission}
            editDisabled={!hasEditPermission}
            deleteDisabled={!hasDeletePermission}
            findDisabled={!hasFindPermission}
            dataFound={activeTab === "group" ? isEditingGroup : isEditingUser}
            onSave={activeTab === "group" ? saveGroup : saveUser}
            onClear={clearForm}
            onEdit={activeTab === "group" ? saveGroup : saveUser}
            onDelete={activeTab === "group" ? deleteGroup : deleteUser}
            onExit={onClose}
          />
        </div>

        {/* Sub-Header: Segmented Tabs & Mode Status */}
        <div className="px-5 py-2.5 bg-white border-b border-slate-200 flex items-center justify-between gap-4">
          {/* Pill Tabs */}
          <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg border border-slate-200/80">
            <button
              type="button"
              onClick={() => setActiveTab("group")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-xs font-bold transition cursor-pointer ${
                activeTab === "group"
                  ? "bg-[#1e73be] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
              }`}
            >
              <FiShield size={14} />
              <span>Roles & Permissions</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${activeTab === "group" ? "bg-blue-800 text-white" : "bg-slate-200 text-slate-700"}`}>
                {groupsList.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("user")}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-md text-xs font-bold transition cursor-pointer ${
                activeTab === "user"
                  ? "bg-[#1e73be] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
              }`}
            >
              <FiUsers size={14} />
              <span>User Accounts</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${activeTab === "user" ? "bg-blue-800 text-white" : "bg-slate-200 text-slate-700"}`}>
                {fullUsersDirectory.length}
              </span>
            </button>
          </div>

          {/* Active Operation Status Indicator */}
          <div className="flex items-center gap-2 text-xs">
            {activeTab === "group" ? (
              isEditingGroup ? (
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                  Editing Role:{" "}
                  <strong className="font-bold text-amber-900">
                    {groupData.groupName?.label || groupData.groupName}
                  </strong>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  Creating New Role
                </span>
              )
            ) : isEditingUser ? (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-semibold">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                Editing User:{" "}
                <strong className="font-bold text-amber-900">
                  {userData.userName?.label || userData.userName}
                </strong>
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                Registering New User
              </span>
            )}
          </div>
        </div>

        {/* ===================== TAB 1: ROLES & PERMISSIONS ===================== */}
        {activeTab === "group" && (
          <div className="flex-1 min-h-0 flex gap-4 p-4 bg-slate-50/50 overflow-hidden">
            {/* Left Column: Role Details & Catalog */}
            <div className="w-[360px] shrink-0 flex flex-col gap-3 h-full overflow-y-auto">
              {/* Role Definition Card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <FiShield className="text-blue-600" /> Role Configuration
                  </span>
                  {isEditingGroup && (
                    <button
                      type="button"
                      onClick={handleClearGroup}
                      className="text-[11px] text-blue-600 hover:text-blue-800 font-bold cursor-pointer"
                    >
                      + New Role
                    </button>
                  )}
                </div>

                {/* Role Name */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-700">Role / Group Name:</label>
                  <SherComboBox
                    options={groupsList}
                    value={groupData.groupName?.label || groupData.groupName || ""}
                    placeholder="Enter or select Role Name..."
                    onSelect={(opt) => handleLoadGroup(opt)}
                    onInputChange={(txt) => setGroupData((prev) => ({ ...prev, groupName: txt }))}
                    onClear={handleClearGroup}
                    className="w-full"
                    inputClassName={inputClass}
                    inputHeight="28px"
                  />
                </div>

                {/* Master Global Grants */}
                <div className="flex flex-col gap-1.5 pt-1">
                  <span className="text-[11px] font-bold text-slate-600">Global Action Permissions:</span>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition">
                      <input
                        type="checkbox"
                        checked={groupData.savePermission}
                        onChange={(e) =>
                          setGroupData((prev) => ({ ...prev, savePermission: e.target.checked }))
                        }
                        className="rounded text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-xs font-bold text-emerald-800">Allow Save</span>
                    </label>

                    <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition">
                      <input
                        type="checkbox"
                        checked={groupData.editPermission}
                        onChange={(e) =>
                          setGroupData((prev) => ({ ...prev, editPermission: e.target.checked }))
                        }
                        className="rounded text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-xs font-bold text-amber-800">Allow Edit</span>
                    </label>

                    <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition">
                      <input
                        type="checkbox"
                        checked={groupData.deletePermission}
                        onChange={(e) =>
                          setGroupData((prev) => ({ ...prev, deletePermission: e.target.checked }))
                        }
                        className="rounded text-rose-600 focus:ring-rose-500"
                      />
                      <span className="text-xs font-bold text-rose-800">Allow Delete</span>
                    </label>

                    <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition">
                      <input
                        type="checkbox"
                        checked={groupData.findPermission}
                        onChange={(e) =>
                          setGroupData((prev) => ({ ...prev, findPermission: e.target.checked }))
                        }
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs font-bold text-blue-800">Allow Find</span>
                    </label>
                  </div>
                </div>

                {/* Quick Save / Reset Buttons */}
                <div className="flex items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={saveGroup}
                    className="flex-1 py-1.5 rounded-lg text-xs font-bold text-white bg-[#1e73be] hover:bg-blue-700 shadow-sm transition cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <FiCheck /> {isEditingGroup ? "Update Role" : "Save Role"}
                  </button>
                  <button
                    type="button"
                    onClick={handleClearGroup}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-300 transition cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Roles Catalog Quick-Selector Card */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex-1 flex flex-col min-h-[220px]">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider pb-2 border-b border-slate-100 block mb-2">
                  Existing Roles Catalog ({groupsList.length})
                </span>

                <div className="flex-1 overflow-y-auto flex flex-col gap-1.5 pr-1">
                  {groupsList.map((g) => {
                    const isSelected = selectedGroupId === g.Auto;
                    return (
                      <div
                        key={g.Auto}
                        onClick={() => handleLoadGroup(g)}
                        className={`flex items-center justify-between p-2.5 rounded-lg border transition cursor-pointer ${
                          isSelected
                            ? "bg-blue-50 border-blue-400 text-blue-900 font-bold shadow-xs"
                            : "bg-slate-50/60 border-slate-200 text-slate-700 hover:bg-blue-50/40 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-blue-600" />
                          <span className="text-xs">{g.Name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Edit Permissions"
                            className="p-1 hover:text-blue-700 text-slate-400 cursor-pointer"
                          >
                            <FiEdit2 size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column: Menu Permissions Tree Matrix */}
            <div className="flex-1 h-full bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col overflow-hidden">
              {/* Tree Control Bar */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-200 gap-3">
                {/* Search Bar */}
                <div className="relative flex-1 max-w-sm">
                  <input
                    type="text"
                    placeholder="Search menus, forms or actions (e.g. Sales, Scheme)..."
                    value={menuSearchQuery}
                    onChange={(e) => setMenuSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-7 py-1.5 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white"
                  />
                  <FiSearch className="absolute left-2.5 top-2 text-slate-400 text-sm" />
                  {menuSearchQuery && (
                    <button
                      onClick={() => setMenuSearchQuery("")}
                      className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 text-xs px-1"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Batch Action Quick Toggles */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleSelectAll(true)}
                    className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold border border-slate-200 cursor-pointer"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectAll(false)}
                    className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold border border-slate-200 cursor-pointer"
                  >
                    Unselect All
                  </button>
                  <button
                    type="button"
                    onClick={expandAllNodes}
                    className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold border border-slate-200 cursor-pointer"
                  >
                    Expand All
                  </button>
                  <button
                    type="button"
                    onClick={collapseAllNodes}
                    className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-bold border border-slate-200 cursor-pointer"
                  >
                    Collapse All
                  </button>
                </div>
              </div>

              {/* Action Grant Filters Bar */}
              <div className="py-2 px-1 flex items-center justify-between border-b border-slate-100 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-600">Quick Grants:</span>
                  <button
                    type="button"
                    onClick={() => handleSelectAllAction("Save", true)}
                    className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 cursor-pointer"
                  >
                    + All Save
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectAllAction("Edit", true)}
                    className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 cursor-pointer"
                  >
                    + All Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectAllAction("Delete", true)}
                    className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 cursor-pointer"
                  >
                    + All Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectAllAction("Find", true)}
                    className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 cursor-pointer"
                  >
                    + All Find
                  </button>
                </div>

                <div className="font-medium">
                  Permissions Active:{" "}
                  <strong className="text-blue-700 font-bold">
                    {grantedStats.granted}
                  </strong>{" "}
                  / {grantedStats.total}
                </div>
              </div>

              {/* Tree Matrix Scroll Container */}
              <div className="flex-1 overflow-y-auto pr-2 mt-2">
                {isTreeLoading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-7 h-7 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs text-slate-500 font-medium">
                        Loading Menu Permissions Tree...
                      </span>
                    </div>
                  </div>
                ) : filteredTree.length === 0 ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2 py-16">
                    <FiShield size={32} className="text-slate-300" />
                    <span className="text-xs">
                      {menuTree.length === 0
                        ? "Select a role from the left catalog to inspect or edit permissions."
                        : "No menus matching the search query."}
                    </span>
                  </div>
                ) : (
                  filteredTree.map((node) => <TreeNode key={node.id} node={node} depth={0} />)
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===================== TAB 2: USER ACCOUNTS ===================== */}
        {activeTab === "user" && (
          <div className="flex-1 min-h-0 flex gap-4 p-4 bg-slate-50/50 overflow-hidden">
            {/* Left Column: User Profile Editor Form */}
            <div className="w-[390px] shrink-0 bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col gap-3 h-full overflow-y-auto">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <FiUser className="text-blue-600" /> User Credentials
                </span>
                {isEditingUser && (
                  <button
                    type="button"
                    onClick={handleClearUser}
                    className="text-[11px] text-blue-600 hover:text-blue-800 font-bold cursor-pointer"
                  >
                    + New User
                  </button>
                )}
              </div>

              {/* Username Input */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-700">Username / Login ID:</label>
                <SherComboBox
                  comboRef={userNameRef}
                  options={usersList.length > 100 ? usersList.slice(0, 100) : usersList}
                  value={userData.userName?.label || userData.userName || ""}
                  placeholder="Enter or select username..."
                  onSelect={(opt) => handleLoadUser(opt)}
                  onInputChange={(txt) => setUserData((prev) => ({ ...prev, userName: txt }))}
                  onClear={handleClearUser}
                  className="w-full"
                  inputClassName={inputClass}
                  inputHeight="28px"
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-700">Password:</label>
                  {isEditingUser && (
                    <span className="text-[10px] text-slate-400">Leave blank to keep existing</span>
                  )}
                </div>
                <div className="relative w-full">
                  <input
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    value={userData.password}
                    onChange={(e) => setUserData((prev) => ({ ...prev, password: e.target.value }))}
                    placeholder={isEditingUser ? "••••••••" : "Enter secure password..."}
                    className={`${inputClass} h-[28px] pr-8`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-700">Confirm Password:</label>
                <div className="relative w-full">
                  <input
                    ref={confirmPasswordRef}
                    type={showConfirmPassword ? "text" : "password"}
                    value={userData.confirmPassword}
                    onChange={(e) =>
                      setUserData((prev) => ({ ...prev, confirmPassword: e.target.value }))
                    }
                    placeholder={isEditingUser ? "••••••••" : "Confirm secure password..."}
                    className={`${inputClass} h-[28px] pr-8`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    {showConfirmPassword ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                  </button>
                </div>

                {/* Password Match Indicator */}
                {userData.password && userData.confirmPassword && (
                  <span
                    className={`text-[10.5px] font-semibold mt-0.5 flex items-center gap-1 ${
                      userData.password === userData.confirmPassword
                        ? "text-emerald-600"
                        : "text-rose-600"
                    }`}
                  >
                    {userData.password === userData.confirmPassword ? (
                      <>
                        <FiCheck /> Passwords match
                      </>
                    ) : (
                      <>
                        <FiX /> Passwords do not match
                      </>
                    )}
                  </span>
                )}
              </div>

              {/* Assigned Role / Group */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-700">Assigned Role / Group:</label>
                <SherComboBox
                  comboRef={underGroupRef}
                  options={groupsList}
                  value={userData.underGroup?.label || ""}
                  placeholder="Select Role..."
                  onSelect={(opt) => setUserData((prev) => ({ ...prev, underGroup: opt }))}
                  onClear={() => setUserData((prev) => ({ ...prev, underGroup: null }))}
                  className="w-full"
                  inputClassName={inputClass}
                  inputHeight="28px"
                />
              </div>

              {/* Linked Employee / Salesman */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-700">Linked Employee / Staff:</label>
                <SherComboBox
                  comboRef={employeeRef}
                  options={employeesList}
                  value={userData.employee?.label || ""}
                  placeholder="Select Employee..."
                  onSelect={(opt) => setUserData((prev) => ({ ...prev, employee: opt }))}
                  onClear={() => setUserData((prev) => ({ ...prev, employee: null }))}
                  className="w-full"
                  inputClassName={inputClass}
                  inputHeight="28px"
                />
              </div>

              {/* Location / Branch */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-700">Assigned Location / Branch:</label>
                <SherComboBox
                  comboRef={locationRef}
                  options={locationsList}
                  value={userData.location?.label || ""}
                  placeholder="Select Branch Location..."
                  onSelect={(opt) => setUserData((prev) => ({ ...prev, location: opt }))}
                  onClear={() => setUserData((prev) => ({ ...prev, location: null }))}
                  className="w-full"
                  inputClassName={inputClass}
                  inputHeight="28px"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-3 mt-auto border-t border-slate-100">
                <button
                  type="button"
                  onClick={saveUser}
                  className="flex-1 py-1.5 rounded-lg text-xs font-bold text-white bg-[#1e73be] hover:bg-blue-700 shadow-sm transition cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <FiCheck /> {isEditingUser ? "Update User" : "Save User"}
                </button>
                {isEditingUser && (
                  <button
                    type="button"
                    onClick={deleteUser}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-rose-600 hover:bg-rose-50 border border-rose-300 transition cursor-pointer flex items-center gap-1"
                  >
                    <FiTrash2 /> Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClearUser}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-300 transition cursor-pointer"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Right Column: Users Directory Table */}
            <div className="flex-1 h-full bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col overflow-hidden">
              {/* Directory Header Bar */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-200 gap-3">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-800 text-sm">System Users Directory</span>
                  <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2.5 py-0.5 rounded-full text-[11px] font-semibold">
                    {filteredUsers.length} Users
                  </span>
                </div>

                <div className="relative w-64">
                  <input
                    type="text"
                    placeholder="Search users by name or role..."
                    value={userDirectorySearch}
                    onChange={(e) => setUserDirectorySearch(e.target.value)}
                    className="w-full pl-8 pr-7 py-1 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white h-[28px]"
                  />
                  <FiSearch className="absolute left-2.5 top-2 text-slate-400 text-xs" />
                  {userDirectorySearch && (
                    <button
                      onClick={() => setUserDirectorySearch("")}
                      className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 text-xs px-1"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Users Table Container with Continuous Infinite Scroll */}
              <div
                ref={tableContainerRef}
                onScroll={handleScroll}
                onKeyDown={handleTableKeyDown}
                tabIndex={0}
                className="flex-1 overflow-auto border border-slate-200 rounded-lg mt-3 text-xs outline-none select-none"
              >
                <table className="w-full border-collapse text-left">
                  <thead className="bg-[#1e73be] text-white text-[11px] font-bold sticky top-0 z-10 shadow-xs">
                    <tr>
                      <th className="px-3 py-2 border-r border-blue-400 w-12 text-center">#</th>
                      <th className="px-3 py-2 border-r border-blue-400">Username</th>
                      <th className="px-3 py-2 border-r border-blue-400">Role / Group</th>
                      <th className="px-3 py-2 border-r border-blue-400">Linked Staff</th>
                      <th className="px-3 py-2 border-r border-blue-400">Branch Location</th>
                      <th className="px-3 py-2 text-center w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {displayedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-400 text-xs">
                          No users found.
                        </td>
                      </tr>
                    ) : (
                      displayedUsers.map((u, idx) => {
                        const rowNumber = idx + 1;
                        const isSelected = selectedUserId === u.auto;
                        const empLabel = employeesMap.get(u.agentCode) || (u.agentCode ? `Agent #${u.agentCode}` : "-");
                        const locVal = u.location ?? u.Location;
                        const locLabel = locationsMap.get(Number(locVal)) || locVal || "-";

                        return (
                          <tr
                            key={u.auto || rowNumber}
                            data-row-index={idx}
                            onClick={() => handleLoadUser(u)}
                            className={`hover:bg-blue-50/50 cursor-pointer transition-colors ${
                              isSelected
                                ? "bg-blue-100 font-bold text-blue-900"
                                : idx % 2 === 0
                                ? "bg-white"
                                : "bg-[#f2f7ff]"
                            }`}
                          >
                            <td className="px-3 py-2 text-center text-slate-500 font-Inter font-semibold text-[11px] border-r border-slate-200">
                              {rowNumber}
                            </td>
                            <td className="px-3 py-2 border-r border-slate-200 font-bold text-slate-800">
                              <div className="flex items-center gap-1.5">
                                <FiUser className="text-blue-600 shrink-0" />
                                <span>{u.userName || `User #${u.auto}`}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 border-r border-slate-200">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                {u.userType || "COMMON"}
                              </span>
                            </td>
                            <td className="px-3 py-2 border-r border-slate-200 text-slate-700">
                              {empLabel}
                            </td>
                            <td className="px-3 py-2 border-r border-slate-200 text-slate-700">
                              <div className="flex items-center gap-1">
                                <FiMapPin className="text-slate-400 text-xs shrink-0" />
                                <span>{locLabel}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLoadUser(u);
                                }}
                                className="px-2 py-0.5 rounded text-[11px] font-bold text-blue-600 hover:bg-blue-100 transition cursor-pointer"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Scrolling Pagination Status Bar (Matching Other Reports) */}
              <div className="flex items-center justify-between pt-2.5 border-t border-slate-200 text-xs text-slate-600 font-Inter mt-2 shrink-0">
                <div className="flex items-center gap-2">
                  <span>
                    Showing <strong className="text-slate-800 font-bold">{Math.min(visibleCount, filteredUsers.length)}</strong> of{" "}
                    <strong className="text-blue-700 font-bold">{filteredUsers.length}</strong> users
                  </span>
                  {hasMore && (
                    <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-semibold border border-blue-100">
                      Scroll down to load more
                    </span>
                  )}
                </div>

                {hasMore && (
                  <button
                    type="button"
                    onClick={loadAll}
                    className="text-[11px] font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                  >
                    Load All ({filteredUsers.length})
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateUser;
